import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { LtiPlatform } from '../api/types';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';

interface FormState {
  displayName: string;
  iss: string;
  clientId: string;
  authLoginUrl: string;
  authTokenUrl: string;
  keySetUrl: string;
  deploymentIds: string;
}

function toForm(platform: LtiPlatform | undefined): FormState {
  return {
    displayName: platform?.displayName ?? '',
    iss: platform?.iss ?? '',
    clientId: platform?.clientId ?? '',
    authLoginUrl: platform?.authLoginUrl ?? '',
    authTokenUrl: platform?.authTokenUrl ?? '',
    keySetUrl: platform?.keySetUrl ?? '',
    // Nhập nhiều dòng thay vì dấu phẩy: deployment ID của Moodle có thể chứa
    // dấu phẩy, và tách sai thì launch hỏng với thông báo rất mơ hồ.
    deploymentIds: (platform?.deploymentIds ?? []).join('\n'),
  };
}

/**
 * Thêm/sửa nền tảng LTI.
 *
 * Kiểm dữ liệu thật nằm ở backend (HTTPS bắt buộc, trùng iss+client_id, …) và
 * lỗi quay về dưới dạng `code` đã dịch. Ở đây chỉ đánh dấu trường bắt buộc để
 * người dùng không phải gửi mới biết mình bỏ trống.
 */
export function PlatformDialog({
  platform,
  onClose,
  onSaved,
}: {
  platform: LtiPlatform | undefined;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<FormState>(() => toForm(platform));

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const save = useMutation<FormState, { id: string }>(
    useCallback(
      (input: FormState) => {
        const body = {
          displayName: input.displayName,
          iss: input.iss,
          clientId: input.clientId,
          authLoginUrl: input.authLoginUrl,
          authTokenUrl: input.authTokenUrl,
          keySetUrl: input.keySetUrl,
          deploymentIds: input.deploymentIds
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
        };
        return platform
          ? apiFetch<{ id: string }>(`/api/admin/lti/platforms/${platform.id}`, {
              method: 'PUT',
              body: { ...body, status: platform.status },
            })
          : apiFetch<{ id: string }>('/api/admin/lti/platforms', { method: 'POST', body });
      },
      [platform],
    ),
  );

  const field = (key: keyof FormState, label: string, hint?: string): ReactNode => (
    <label className="field">
      <span>{label}</span>
      <input
        type={key.endsWith('Url') ? 'url' : 'text'}
        value={form[key]}
        required
        onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
      />
      {hint ? <small className="hint">{hint}</small> : null}
    </label>
  );

  const submit = async (): Promise<void> => {
    const result = await save.run(form);
    if (result) onSaved();
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{platform ? t('lti.editPlatform') : t('lti.addPlatform')}</h2>

      {field('displayName', t('lti.field.displayName'))}
      {field('iss', t('lti.field.iss'))}
      {field('clientId', t('lti.field.clientId'))}
      {field('authLoginUrl', t('lti.field.authLoginUrl'))}
      {field('authTokenUrl', t('lti.field.authTokenUrl'))}
      {field('keySetUrl', t('lti.field.keySetUrl'))}

      <label className="field">
        <span>{t('lti.field.deploymentIds')}</span>
        <textarea
          rows={3}
          value={form.deploymentIds}
          onChange={(event) =>
            setForm((current) => ({ ...current, deploymentIds: event.target.value }))
          }
        />
        <small className="hint">{t('lti.field.deploymentIdsHint')}</small>
      </label>

      {save.error ? <ErrorNotice error={save.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button type="button" disabled={save.pending} onClick={() => void submit()}>
          {t('common:action.save')}
        </button>
      </footer>
    </dialog>
  );
}
